import React, { useContext, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import NestedQuestionList from '../NestedQuestionList';
import { useInstructions, hasInstructionContent } from '../hooks/useInstructions';
import { skipsQuestionIndex } from '../../SubmissionView/submissionResponseUtils';
import { FeedbackContext } from '../FeedbacksRoot/FeedbackContext';

const PURPLE = '#7200e0';
const BADGE_GREY = '#f2f1f3';

// Shared inner content for the question navigation UI: the "Questions" header
// (with View instructions / View all questions actions), the nested question
// list, and the instructions modal. Rendered both inside the header dropdown
// popup (QuestionNavigationPanel) and inline in the Task Details sidebar tab.
const QuestionNavigationContent = ({
  questions = [],
  currentQuestionIndex = 0,
  submission,
  onQuestionSelect,
  onClose,
}) => {
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const instructionsRef = useRef(null);
  const history = useHistory();
  const location = useLocation();
  const { methods } = useContext(FeedbackContext);
  const {
    html: instructionsHtml,
    isLoading: isLoadingInstructions,
    hasInstructions,
  } = useInstructions(submission);

  const getSubmissionId = () =>
    location.pathname.split('/').find((part) => part.match(/^[a-f0-9-]{36}$/i));

  const navigateToOverview = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const submissionId = submission?.id || getSubmissionId();
    if (submissionId && !isLeaving) {
      setIsLeaving(true);
      const saved = await methods?.prepareToLeaveEditor?.();
      if (saved === false) {
        setIsLeaving(false);
        return;
      }
      history.push(`/submissions/${submissionId}`);
    }
  };

  const openInstructions = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsInstructionsOpen(true);
  };

  const renderInstructionsModal = () => {
    if (!isInstructionsOpen) return null;

    return ReactDOM.createPortal(
      <InstructionsOverlay onClick={() => setIsInstructionsOpen(false)}>
        <InstructionsPanel
          ref={instructionsRef}
          role="dialog"
          aria-label="Instructions"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CloseButton
            type="button"
            onClick={() => setIsInstructionsOpen(false)}
            aria-label="Close instructions"
          >
            ×
          </CloseButton>
          {isLoadingInstructions ? (
            <InstructionsContent>Loading instructions...</InstructionsContent>
          ) : hasInstructionContent(instructionsHtml) ? (
            <InstructionsContent
              dangerouslySetInnerHTML={{ __html: instructionsHtml }}
            />
          ) : (
            <InstructionsContent>
              No instructions provided for this task.
            </InstructionsContent>
          )}
        </InstructionsPanel>
      </InstructionsOverlay>,
      document.body,
    );
  };


  return (
    <ContentRoot>
      <Header>
        <Title>Questions</Title>
        <HeaderActions onMouseDown={(e) => e.stopPropagation()}>
          {hasInstructions && (
            <InstructionsButton
              type="button"
              onClick={openInstructions}
              onMouseDown={(e) => e.stopPropagation()}
            >
              View instructions
            </InstructionsButton>
          )}
          {/* A one-question task skips the overview entirely (SubmissionViewWrapper),
              so this button would navigate somewhere that immediately redirects back. */}
          {!skipsQuestionIndex(submission) && (
            <ViewAllButton
              type="button"
              onClick={navigateToOverview}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={isLeaving}
            >
              View all questions
            </ViewAllButton>
          )}
        </HeaderActions>
      </Header>

      <NestedQuestionList
        questions={questions}
        currentQuestionIndex={currentQuestionIndex}
        submission={submission}
        onQuestionSelect={onQuestionSelect}
        compact={true}
      />

      {renderInstructionsModal()}
    </ContentRoot>
  );
};

export default QuestionNavigationContent;

const pillButtonReset = css`
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  line-height: 1.3;
  text-decoration: none;
  box-shadow: none;
`;

const ContentRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);

  button {
    ${pillButtonReset}
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #ececec;
  flex-shrink: 0;
  min-width: 0;
`;

const Title = styled.h3`
  font-weight: 700;
  font-size: 18px;
  color: #181718;
  margin: 0;
  line-height: 1.3;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  pointer-events: auto;
`;

const InstructionsButton = styled.button`
  font-weight: 500;
  font-size: 13px;
  color: #4b464f !important;
  background: ${BADGE_GREY} !important;
  border: none !important;
  border-radius: 20px !important;
  padding: 7px 16px !important;
  cursor: pointer !important;
  white-space: nowrap;
  pointer-events: auto !important;
  opacity: 1 !important;

  &:hover {
    background: #ebe9ed !important;
    color: #181718 !important;
  }

  &:active {
    background: #e2e0e4 !important;
  }
`;

const ViewAllButton = styled.button`
  font-weight: 600;
  font-size: 13px;
  color: ${PURPLE} !important;
  background: #ffffff !important;
  border: 1.5px solid ${PURPLE} !important;
  border-radius: 20px !important;
  padding: 7px 16px !important;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f5f0fa !important;
  }
`;

const InstructionsOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(24, 23, 24, 0.4);
  padding: 24px;
  pointer-events: auto;
  cursor: default;
`;

const InstructionsPanel = styled.div`
  position: relative;
  width: min(640px, 100%);
  max-height: min(80vh, 640px);
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(24, 23, 24, 0.14);
  overflow: hidden;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #4b464f;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${BADGE_GREY};
  }
`;

const InstructionsContent = styled.div`
  overflow-y: auto;
  max-height: min(80vh, 640px);
  padding: 20px 48px 24px 24px;
  color: #4b5563;
  line-height: 1.6;
  font-size: 14px;

  p {
    margin: 0 0 8px;
  }
`;
